import { CID } from 'multiformats/cid';

import type { HeliaNode } from '@/types/poc/helia';

const DEFAULT_CID_PREFIX_LENGTH = 12;
const DEFAULT_CID_SUFFIX_LENGTH = 6;

export function parseCid(cid: string): CID {
  return CID.parse(cid);
}

export function isValidCid(value: string): boolean {
  try {
    parseCid(value);
    return true;
  } catch {
    return false;
  }
}

export function normalizeCid(cid: string | CID): string {
  return typeof cid === 'string' ? parseCid(cid).toString() : cid.toString();
}

export function shortenCid(
  cid: string | CID,
  prefixLength = DEFAULT_CID_PREFIX_LENGTH,
  suffixLength = DEFAULT_CID_SUFFIX_LENGTH
): string {
  const normalized = normalizeCid(cid);
  if (normalized.length <= prefixLength + suffixLength) {
    return normalized;
  }

  return `${normalized.slice(0, prefixLength)}...${normalized.slice(-suffixLength)}`;
}

export function filenameFromCid(cid: string | CID, extension?: string): string {
  const normalized = normalizeCid(cid);
  return extension == null ? normalized : `${normalized}.${extension.replace(/^\./u, '')}`;
}

export async function createCID(data: Uint8Array): Promise<CID> {
  const { sha256 } = await import('multiformats/hashes/sha2');
  const raw = await import('multiformats/codecs/raw');

  const hash = await sha256.digest(data);
  return CID.create(1, raw.code, hash);
}

export async function validateCIDExistsInHelia(
  node: HeliaNode,
  cid: string | CID
): Promise<boolean> {
  const parsed = typeof cid === 'string' ? parseCid(cid) : cid;

  try {
    await node.fs.stat(parsed);
    return true;
  } catch {
    return false;
  }
}

export async function getCIDData(node: HeliaNode, cid: string | CID): Promise<Uint8Array> {
  const parsed = typeof cid === 'string' ? parseCid(cid) : cid;
  const chunks: Uint8Array[] = [];

  for await (const chunk of node.fs.cat(parsed)) {
    chunks.push(chunk);
  }

  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(totalLength);

  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}
